import React from 'react';
import * as Icons from '@ant-design/icons';
import type { BaseComponentProps } from '../../types/common';

export interface AndIconProps extends BaseComponentProps {
  /** 图标名称（Ant Design Icons） */
  name?: string;
  /** 自定义 SVG 代码，存在时优先渲染 SVG */
  svg?: string;
  /** 图标大小 */
  size?: number;
  /** 图标颜色 */
  color?: string;
  /** 是否旋转动画 */
  spin?: boolean;
  /** 旋转角度（度） */
  rotate?: number;
  /** 双色图标第二色（仅 Ant Design 双色图标） */
  twoToneColor?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 组件样式配置 */
  styles?: React.CSSProperties;
  /** 点击事件 */
  onClick?: () => void;
}

const SPIN_KEYFRAMES = `@keyframes and-icon-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`;

/** 使 SVG 继承外层 color：未设置 fill 的 path 等使用 currentColor */
const SVG_COLOR_STYLE = `.and-icon-svg-root svg,.and-icon-svg-root svg path{fill:currentColor}.and-icon-svg-root svg{color:inherit}`;

export const AndIcon: React.FC<AndIconProps> = ({
  id: _id,
  sessionId: _sessionId,
  develop: _develop,
  name,
  svg: svgRaw,
  size = 16,
  color,
  spin = false,
  rotate = 0,
  twoToneColor,
  style,
  styles,
  onClick,
}) => {
  const svg = typeof svgRaw === 'string' ? svgRaw.trim() : '';

  const mergedStyle: React.CSSProperties = {
    fontSize: size,
    color,
    cursor: onClick ? 'pointer' : undefined,
    ...styles,
    ...style,
  };

  if (svg) {
    const wrapperStyle: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      transform: rotate ? `rotate(${rotate}deg)` : undefined,
      ...mergedStyle,
    };
    const innerStyle: React.CSSProperties = spin
      ? { animation: 'and-icon-spin 1s linear infinite', display: 'inline-flex' }
      : { display: 'inline-flex' };
    return (
      <>
        <style>{SVG_COLOR_STYLE}</style>
        {spin && <style>{SPIN_KEYFRAMES}</style>}
        <span
          className="and-icon-svg-root"
          style={wrapperStyle}
          onClick={onClick}
          role={onClick ? 'button' : undefined}
        >
          <span style={innerStyle}>
            <span
              dangerouslySetInnerHTML={{ __html: svg }}
              style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            />
          </span>
        </span>
      </>
    );
  }

  if (!name) return null;

  const IconComponent = (Icons as any)[name];
  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in @ant-design/icons`);
    return null;
  }

  return (
    <IconComponent
      style={mergedStyle}
      spin={spin}
      rotate={rotate}
      twoToneColor={twoToneColor || undefined}
      onClick={onClick}
    />
  );
};

export default AndIcon;
